import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Check } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { Chip, SampleTag, EventCard, StoryCard } from "../components/cards";
import { getActivityBySlug, getActivityEvents, getActivityStories } from "../lib/content";

export default function ActivityDetail() {
  const { slug } = useParams();
  const activity = getActivityBySlug(slug);

  if (!activity) {
    return (
      <main className="pt-40 pb-32 text-center" data-testid="activity-not-found">
        <h1 className="font-serif text-4xl text-ink">Activity not found</h1>
        <Link to="/activities" className="mt-6 inline-block text-action font-semibold underline underline-offset-4">
          Back to all activities
        </Link>
      </main>
    );
  }

  const relatedEvents = getActivityEvents(slug);
  const relatedStories = getActivityStories(slug);

  return (
    <main>
      <SEO title={activity.title} description={activity.summary} path={`/activities/${slug}`} image={activity.image} />

      <article className="pt-32 md:pt-40 pb-24" data-testid="activity-detail">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link
            to="/activities"
            data-testid="activity-back"
            className="inline-flex items-center gap-2 text-sm font-medium text-ink-2 hover:text-ink transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> All activities
          </Link>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Chip pillar={activity.pillar} />
            <span className="text-[0.7rem] font-semibold uppercase tracking-[0.15em] text-action border border-action/30 rounded-full px-3 py-1">
              {activity.status}
            </span>
            {activity.isSample && <SampleTag />}
          </div>

          <h1 className="mt-5 font-serif tracking-tighter leading-[0.98] text-ink text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
            {activity.title}
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-ink-2">
            {activity.summary}
          </p>

          <Reveal className="mt-12">
            <div className="overflow-hidden rounded-xl aspect-[21/9]">
              <img src={activity.image} alt={activity.imageAlt} className="h-full w-full object-cover" />
            </div>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-8">
              <p className="text-base md:text-lg leading-relaxed text-ink-2 max-w-2xl">
                {activity.description}
              </p>
              {activity.details && (
                <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl" data-testid="activity-details">
                  {activity.details.map((d) => (
                    <li key={d} className="flex items-start gap-3 border border-line rounded-lg bg-sand-2 px-5 py-4 text-sm text-ink-2">
                      <Check className="h-4 w-4 mt-0.5 shrink-0 text-action" /> {d}
                    </li>
                  ))}
                </ul>
              )}
              {activity.progress && (() => {
                const pct = Math.round((activity.progress.current / activity.progress.goal) * 100);
                const goalTick = Math.min(97, (activity.progress.goal / activity.progress.current) * 100);
                return (
                  <div className="mt-10 max-w-2xl border border-line rounded-xl bg-sand-2 p-7 md:p-8" data-testid="activity-progress">
                    <p className="text-xs uppercase tracking-[0.2em] font-semibold text-ink-2">Live progress</p>
                    <p className="mt-3 font-serif text-4xl md:text-5xl tracking-tight text-ink">
                      {activity.progress.current.toLocaleString("en-IN")}{" "}
                      <span className="font-sans text-base md:text-lg text-ink-2">steps — and still walking</span>
                    </p>
                    <div className="mt-6">
                      <div className="relative h-2.5 rounded-full bg-line">
                        <div className="absolute inset-y-0 left-0 rounded-full bg-pillar-animals" style={{ width: "100%" }} />
                        <div className="absolute -top-1 -bottom-1 w-0.5 bg-ink rounded-full" style={{ left: `${goalTick}%` }} aria-hidden="true" />
                      </div>
                      <p className="mt-2.5 text-xs text-ink-2">
                        Marker shows the original goal of {activity.progress.goal.toLocaleString("en-IN")} steps — crossed at {pct}% with {activity.progress.daysRemaining} days to go.
                      </p>
                    </div>
                    <p className="mt-4 text-sm leading-relaxed text-ink-2">{activity.progress.note}</p>
                  </div>
                );
              })()}
              {activity.isSample && (
                <p className="mt-8 border border-line rounded-xl bg-sand-2 p-5 text-sm text-ink-2 max-w-2xl">
                  This is a sample activity shown to demonstrate the layout. Real
                  PilCoo initiatives will be published here as they launch.
                </p>
              )}
              <div className="mt-10 flex flex-wrap gap-4">
                {activity.externalUrl ? (
                  <a
                    href={activity.externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${activity.ctaLabel} (opens in new tab)`}
                    data-testid="activity-cta"
                    className="group rounded-full bg-action text-sand px-7 py-3.5 text-sm font-semibold inline-flex items-center gap-2 transition-transform duration-300 hover:scale-[1.04] active:scale-95"
                  >
                    {activity.ctaLabel}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                ) : (
                  <Link
                    to="/get-involved"
                    data-testid="activity-cta"
                    className="group rounded-full bg-action text-sand px-7 py-3.5 text-sm font-semibold inline-flex items-center gap-2 transition-transform duration-300 hover:scale-[1.04] active:scale-95"
                  >
                    {activity.ctaLabel}
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                )}
              </div>
              {activity.communityUrl && (
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <p className="text-sm text-ink-2">Walking with us already?</p>
                  <a
                    href={activity.communityUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Join our Pacer community (opens in new tab)"
                    data-testid="activity-pacer-link"
                    className="inline-flex items-center gap-2 rounded-full border border-ink px-6 py-3 text-sm font-semibold text-ink transition-colors duration-300 hover:bg-ink hover:text-sand"
                  >
                    Join our Pacer Community <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
              )}
            </div>
          </div>

          {relatedEvents.length > 0 && (
            <section className="mt-20" data-testid="activity-events">
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-ink">
                Events in this activity
              </h2>
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                {relatedEvents.map((event) => (
                  <EventCard key={event.slug} event={event} past={event.status === "past"} />
                ))}
              </div>
            </section>
          )}

          {relatedStories.length > 0 && (
            <section className="mt-20" data-testid="activity-stories">
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-ink">
                Related stories
              </h2>
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-10">
                {relatedStories.map((story) => (
                  <StoryCard key={story.slug} story={story} withImage />
                ))}
              </div>
            </section>
          )}
        </div>
      </article>
    </main>
  );
}
